import {
  ComponentIds,
  Entity,
  GuestConnectionsComponent,
  HierarchyUtils,
  Transform,
  WorldTransformComponent
} from '@inter-ikea-core/core';
import {
  Connectors,
  CoreApi,
  ErrorMessages,
  Product,
  ProductType,
  SlidingDoorComponent,
  calculatePartialDistance
} from '@inter-ikea-storageone/range';
import { Dispatch } from 'react';
import { getAutoFillVariants } from 'rangeConfig/shared/utils/autoFill';
import { CommunicationSizeComponent, PlacementResult } from '@inter-ikea-core/core';
import { changeDoorDirection } from 'rangeConfig/shared/utils/doorOpeningDirection';
import { setInvalidSlidingDoorPlacementInfo } from 'ducks/layout';
import { removeProducts } from 'rangeConfig/shared/utils/removeProducts';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';
import { hidePaxCornerFrameOpenCombinationErrorMessageToast } from '../rules/hideMenuRules';

const placementFailedByInvalidConnector = (result: PlacementResult, coreApi: CoreApi) => {
  return (
    result.errors
      .filter(
        val =>
          val.failedRule?.id ===
          coreApi
            .getServices()
            .validations.validations.find(
              validation => validation.id === 'RequiredConnectorsSatisfied'
            )?.id
      )
      .filter(Boolean).length > 0
  );
};

export const autoFillDoorsAtTarget = (
  productId: string,
  autoFillDefault: string[],
  coreApi: CoreApi,
  selectedEntity: Entity,
  products: Product[]
) => {
  const autoFillVariants = getAutoFillVariants(products, productId, autoFillDefault);
  // If the selected entity is part of a multi frame solution like corner solutions
  // we need to find all the related frame objects that we can place doors at
  const relatedFrames = coreApi.entities.findAllRelatedFrames(selectedEntity);
  if (relatedFrames && relatedFrames.length > 1) {
    const doorsToRemove = relatedFrames.flatMap(f => {
      const slidingDoor = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(f, [
        ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
        ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
      ]);
      const hingeDoors = coreApi.entities.getDescendantsWithComponent(
        f,
        ComponentIds.DOOR_COMPONENT_ID
      );
      return slidingDoor ? [slidingDoor] : hingeDoors;
    });

    const placementResults = coreApi.history.historyAction(() => {
      return coreApi.products.autoFillDoors(relatedFrames, doorsToRemove, autoFillVariants);
    });

    placementResults.forEach((result: PlacementResult) => {
      if (!result.valid) {
        const failedByInvalidConnectorOrInvalidFrameType = placementFailedByInvalidConnector(
          result,
          coreApi
        );

        if (!failedByInvalidConnectorOrInvalidFrameType) coreApi.createConflict(result);
      }
    });
  } else {
    const slidingDoor = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(
      selectedEntity,
      [ProductType.PAX_SLIDING_DOOR_REGULAR_ASM, ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM]
    );
    const hingeDoors = coreApi.entities.getDescendantsWithComponent(
      selectedEntity,
      ComponentIds.DOOR_COMPONENT_ID
    );
    const doorsToRemove = slidingDoor ? [slidingDoor] : hingeDoors;

    const placementResults = coreApi.history.historyAction(() => {
      return coreApi.products.autoFillDoors([selectedEntity], doorsToRemove, autoFillVariants);
    });

    placementResults.forEach((result: PlacementResult) => {
      if (!result.valid) {
        const failedByInvalidConnectorOrInvalidFrameType = placementFailedByInvalidConnector(
          result,
          coreApi
        );

        if (!failedByInvalidConnectorOrInvalidFrameType) coreApi.createConflict(result);
      }
    });
  }
};

export const autoFillDoors = (
  productId: string,
  autoFillDefault: string[],
  coreApi: CoreApi,
  dispatch: Dispatch<ReturnType<typeof setInvalidSlidingDoorPlacementInfo>>,
  products: Product[]
) => {
  const autoFillVariants = getAutoFillVariants(products, productId, autoFillDefault);
  const product = products.find(product => product.coreProductId === productId);

  const productIsSlidingDoor =
    product?.type === ProductType.PAX_SLIDING_DOOR_REGULAR_ASM ||
    product?.type === ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM;

  const handles = coreApi.entities
    .getByComponent(ComponentIds.HANDLE_COMPONENT_ID)
    .map((handle: any) => {
      return {
        id: coreApi.entities.findProductEntity(handle.id)?.productId,
        position: coreApi.entities.getWithPosition(handle)?.position
      };
    });

  const placementResults = coreApi.history.historyAction(() => {
    const allFrames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
    const oldDoors = coreApi.entities.getByComponents([
      ComponentIds.DOOR_COMPONENT_ID,
      SlidingDoorComponent.id
    ]);

    const res = coreApi.products.autoFillDoors(allFrames, oldDoors, autoFillVariants);
    handles.forEach((handle: any) => {
      coreApi.products.add(handle.id, handle.position);
    });

    // Add some data to the frames
    const allFramesWithMeta = allFrames.map(frame =>
      Object.assign(frame, {
        position: coreApi.entities.getWorldPosition(frame),
        width: coreApi.entities.getEntityComSize(frame)?.width || 0,
        wall: coreApi.entities.getEntityWall(frame)
      })
    );

    type EntityWithMeta = typeof allFramesWithMeta[number];

    const findAdjacentFrames = (frame: EntityWithMeta) =>
      allFramesWithMeta.filter(
        f =>
          frame.id !== f.id &&
          frame.wall === f.wall &&
          // divide by 1.8 for relaxed proximity constraint
          calculatePartialDistance(f.position, frame.position) <= (f.width + frame.width) / 1.8
      );

    const getFrameWidth = (entity: Entity) =>
      entity.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size.width || 0;

    // Groups of frames which are directly adjacent to each other
    const frameGroups = allFramesWithMeta.reduce((acc, curr) => {
      const adjacentFrames = findAdjacentFrames(curr);
      const adjacentFrameIds = adjacentFrames.map(af => af.id);

      let groupIndex;

      acc.forEach((grp, i) => {
        if (grp.some(gf => adjacentFrameIds.includes(gf.id))) {
          groupIndex = i;
        }
      });

      if (groupIndex !== undefined) {
        acc[groupIndex].push(curr);
        return acc;
      }

      return [...acc, [curr]];
    }, [] as EntityWithMeta[][]);

    const transformDelta = (a: Entity, b: Entity) => {
      const bTransform = b.getComponent<WorldTransformComponent>(
        ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
      )?.transform as Transform;

      return a
        .getComponent<WorldTransformComponent>(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
        ?.transform.delta(bTransform) as Transform;
    };

    // Sort by relative X position (from left to right)
    const frameGroupsSorted = frameGroups.map(grp =>
      grp.sort((a, b) => transformDelta(a, b).position.X)
    );

    const isNewDoor = (entity: Entity) =>
      oldDoors.some(door =>
        coreApi.entities.getWorldPosition(entity).equals(coreApi.entities.getWorldPosition(door))
      );

    frameGroupsSorted.forEach(grp => {
      const frameWidths = grp.map(getFrameWidth);

      const sum = (arr: number[]) => arr.reduce((acc, curr) => acc + curr, 0);

      const halfWay = sum(frameWidths) / 2;
      const middleFrame = grp.find((_frame, i) => sum(frameWidths.slice(0, i + 1)) > halfWay);

      if (!middleFrame) return;

      grp.forEach(frame => {
        const placedEntities = res
          .map(({ placedEntity }) => placedEntity)
          .filter(Boolean) as Entity[];

        const door = placedEntities
          .filter(entity => !isNewDoor(entity) && !entity.hasComponent(SlidingDoorComponent.id))
          .find(entity => entity && HierarchyUtils.isDescendantOf(entity, frame));

        if (!door) return;

        const config = {
          frameLeftSeriesConnector: Connectors.DOOR_LEFT,
          frameRightSeriesConnector: Connectors.DOOR_RIGHT,
          frontConnectorLeft: Connectors.DOOR_CONNECTION_LEFT,
          frontConnectorRight: Connectors.DOOR_CONNECTION_RIGHT
        };

        // If the X delta relative to middle frame is <= 0, we've gone
        // past the middle position of the frame group
        const relativeToMiddle = transformDelta(middleFrame, frame);
        const shouldBeRightAligned = relativeToMiddle.position.X <= 0;
        const currentDoorConnector = door.getComponent<GuestConnectionsComponent>(
          ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID
        )?.highestPriorityConnection?.guestConnector.key;

        if (
          (shouldBeRightAligned && currentDoorConnector === config.frontConnectorLeft) ||
          (!shouldBeRightAligned && currentDoorConnector === config.frontConnectorRight)
        ) {
          changeDoorDirection(door, coreApi, config);
        }
      });
    });

    return res;
  });

  const determinePlacementAttemptErrorMessage = () => {
    const cornerFramesInScene = coreApi.entities.getByComponent(CornerSolutionComponent.id);

    // Check if product is TONSTAD w. glass doors, VIKANES or ÅHEIM
    const productIsNotValidForCornerSolution =
      product && product.meta?.allowOnCornerAddonFrame === false;

    const failedRules = placementResults
      .map((r: PlacementResult) => r.errors.map(e => e.failedRule).flat())
      .flat()
      .filter((r: any) => r !== null);

    if (!hideNoFramesInSceneErrorMessage(coreApi)) {
      return ErrorMessages.NoFramesInScene;
    }
    if (cornerFramesInScene.length > 0 && productIsSlidingDoor) {
      return ErrorMessages.PaxSlidingDoorOnCorner;
    }
    if (cornerFramesInScene.length > 0 && productIsNotValidForCornerSolution) {
      return ErrorMessages.PaxDoorAppliedButNotFittingCorner;
    }
    if (!hidePaxCornerFrameOpenCombinationErrorMessageToast(coreApi)) {
      return ErrorMessages.PaxCornerFrameOpenCombination;
    }
    if (productIsSlidingDoor && failedRules.length > 0) {
      return ErrorMessages.PaxSlidingDoorApplicableOnSomeFrames;
    }
    return null;
  };
  const placementAttemptErrorMessage = determinePlacementAttemptErrorMessage();
  if (placementAttemptErrorMessage) {
    coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
  }

  placementResults.forEach((result: PlacementResult) => {
    if (!result.valid) {
      // If the placement fails by having an invalid connector, we shouldn't have to care about the collisions as the entity will not be added (not 100 if this actually the case)
      // The problem is that core instantiates the collision validation before this validation which causes it to trigger a collision, even though no door should be added due to invalid connector
      // This is not a good solution, but we had a case where if you added a sliding door from the menu to all frames, if you have frames with lower depth at the outer
      // sides of the frames that can have a sliding door, it would cause collisions with the middle frames interiors
      // Try this vpc Q43HLG without this check.
      const failedByInvalidConnectorOrInvalidFrameType = placementFailedByInvalidConnector(
        result,
        coreApi
      );

      if (!failedByInvalidConnectorOrInvalidFrameType) {
        coreApi.createConflict(result);
      }
    }
  });
  const shouldSpawnPlacementInfo = placementResults.some((result: PlacementResult) => result.valid);

  if (productIsSlidingDoor) {
    dispatch(setInvalidSlidingDoorPlacementInfo(!shouldSpawnPlacementInfo));
  }

  const slidingDoors = coreApi.entities.getByProductTypes([
    ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
    ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
  ]);

  if (slidingDoors) {
    const frames = slidingDoors
      .flatMap((door: Entity) => {
        const entitygroup = HierarchyUtils.parent(door);
        return (
          entitygroup &&
          HierarchyUtils.children(entitygroup).filter(entity =>
            entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
          )
        );
      })
      .filter(Boolean) as Entity[];

    const valetHangersOnFrames = frames.flatMap(
      frame =>
        frame && coreApi.entities.getChildrenWithProductType(frame, ProductType.PAX_VALET_HANGER)
    );

    if (valetHangersOnFrames) {
      removeProducts(valetHangersOnFrames, coreApi, products);
    }
  }
};
